import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { customerShape, paymentsShape, hotelsShape } from 'constants/propTypes';
import { codes } from 'constants/payment';

import { hasHotel as bookingHasHotel } from 'utils/booking';
import CashSplitRefund from '../SplitRefundMethods/CashRefund';
import CardRefund from '../SplitRefundMethods/CardRefund';
import PaypalRefund from '../SplitRefundMethods/PaypalRefund';
import IdealRefund from '../SplitRefundMethods/IdealRefund';
import SofortRefund from '../SplitRefundMethods/SofortRefund';
import StandardSplitRefund from '../SplitRefundMethods/StandardRefund';
import WriteOffSplitRefund from '../SplitRefundMethods/WriteOffRefund';
import BusinessAccountRefund from '../SplitRefundMethods/BusinessAccountRefund';
import EvoucherRefund from '../SplitRefundMethods/EvoucherRefund';
import SocialMediaRefund from '../SplitRefundMethods/SocialMediaRefund';
import LoyaltyPointsRefund from '../SplitRefundMethods/LoyaltyPointsRefund';

import {
  findUniquePaypalPayments,
  findUniqueIdealPayments,
  findUniqueSofortPayments,
  findUniqueSocialMediaPayments,
  getAmountToRefundPerMethod
} from '../Payments.utils';

const RefundComponents = ({
  addPayment,
  addPayoutAndBalance,
  bookingReference,
  currency,
  customer,
  handleRefundSubmit,
  loading,
  method,
  paymentLocation,
  processCardPaymentRefund,
  reference,
  totalPriceToBePaid,
  updatePayments,
  payments,
  isCancelVoucher,
  mainBookingRef,
  isPackage,
  hotels,
  additionalDetails,
  username,
  isRefundingEntireBlueVoucherBooking,
  isOverpaid = false
}) => {
  const hasHotel = isPackage || bookingHasHotel({ hotels });

  if (method === codes.KLARNA_BNPL) {
    return (
      <CardRefund
        bookingReference={bookingReference}
        currency={currency}
        totalPriceToBePaid={totalPriceToBePaid}
        loading={loading}
        paymentLocation={paymentLocation.name}
        isStation={paymentLocation.isStation}
        processCardPaymentRefund={processCardPaymentRefund}
        payments={payments}
        method={codes.KLARNA_BNPL}
        isCancelVoucher={isCancelVoucher}
        mainBookingRef={mainBookingRef}
        hasHotel={hasHotel}
      />
    );
  }

  if (method === codes.BUSINESS_ACCOUNT) {
    return (
      <BusinessAccountRefund
        onRefund={handleRefundSubmit}
        addPayment={addPayment}
        addPayoutAndBalance={addPayoutAndBalance}
        bookingReference={bookingReference}
        currency={currency}
        loading={loading}
        method={method}
        reference={reference}
        totalPriceToBePaid={totalPriceToBePaid}
        updatePayments={updatePayments}
        payments={payments}
        isCancelVoucher={isCancelVoucher}
        mainBookingRef={mainBookingRef}
        isOverpaid={isOverpaid}
      />
    );
  }

  if (method === codes.CASH) {
    return (
      <CashSplitRefund
        addPayment={addPayment}
        addPayoutAndBalance={addPayoutAndBalance}
        bookingReference={bookingReference}
        currency={currency}
        customer={customer}
        loading={loading}
        method={method}
        reference={reference}
        totalPriceToBePaid={totalPriceToBePaid}
        updatePayments={updatePayments}
        isCancelVoucher={isCancelVoucher}
        mainBookingRef={mainBookingRef}
        isOverpaid={isOverpaid}
      />
    );
  }

  if (method === codes.WRITE_OFF) {
    return (
      <WriteOffSplitRefund
        loading={loading}
        customer={customer}
        bookingReference={bookingReference}
        reference={reference}
        currency={currency}
        addPayment={addPayment}
        addPayoutAndBalance={addPayoutAndBalance}
        updatePayments={updatePayments}
        totalPriceToBePaid={totalPriceToBePaid}
        method={method}
        isCancelVoucher={isCancelVoucher}
        mainBookingRef={mainBookingRef}
        isOverpaid={isOverpaid}
      />
    );
  }

  if (method === codes.EVOUCHER || method === codes.VOUCHER) {
    return (
      <EvoucherRefund
        currency={currency}
        totalPriceToBePaid={totalPriceToBePaid}
        customer={customer}
        bookingReference={bookingReference}
        loading={loading}
      />
    );
  }

  if (method === codes.SOCIAL_MEDIA) {
    return (
      <SocialMediaRefund
        bookingReference={bookingReference}
        currency={currency}
        totalPriceToBePaid={totalPriceToBePaid}
        totalAvailableRefundAmount={getAmountToRefundPerMethod(payments, codes.SOCIAL_MEDIA)}
        payments={findUniqueSocialMediaPayments(payments)}
        processCardPaymentRefund={processCardPaymentRefund}
        paymentLocation={paymentLocation.name}
        isCancelVoucher={isCancelVoucher}
        mainBookingRef={mainBookingRef}
        hasHotel={hasHotel}
      />
    );
  }

  if (method === codes.PAYPAL) {
    return (
      <PaypalRefund
        currency={currency}
        totalPriceToBePaid={totalPriceToBePaid}
        payments={findUniquePaypalPayments(payments)}
        totalAvailableRefundAmount={getAmountToRefundPerMethod(payments, codes.PAYPAL)}
        addPayment={addPayment}
        addPayoutAndBalance={addPayoutAndBalance}
        bookingReference={bookingReference}
        method={method}
        reference={reference}
        updatePayments={updatePayments}
        customer={customer}
        isCancelVoucher={isCancelVoucher}
        mainBookingRef={mainBookingRef}
        processCardPaymentRefund={processCardPaymentRefund}
        paymentLocation={paymentLocation.name}
        hasHotel={hasHotel}
      />
    );
  }

  if (method === codes.CARD) {
    return (
      <CardRefund
        bookingReference={bookingReference}
        currency={currency}
        totalPriceToBePaid={totalPriceToBePaid}
        loading={loading}
        paymentLocation={paymentLocation.name}
        isStation={paymentLocation.isStation}
        processCardPaymentRefund={processCardPaymentRefund}
        payments={payments}
        method={codes.CARD}
        isCancelVoucher={isCancelVoucher}
        mainBookingRef={mainBookingRef}
        hasHotel={hasHotel}
      />
    );
  }

  if (method === codes.APPLE_PAY) {
    return (
      <CardRefund
        bookingReference={bookingReference}
        currency={currency}
        totalPriceToBePaid={totalPriceToBePaid}
        loading={loading}
        paymentLocation={paymentLocation.name}
        isStation={paymentLocation.isStation}
        processCardPaymentRefund={processCardPaymentRefund}
        payments={payments}
        method={codes.APPLE_PAY}
        isCancelVoucher={isCancelVoucher}
        mainBookingRef={mainBookingRef}
        hasHotel={hasHotel}
      />
    );
  }

  if (method === codes.GOOGLE_PAY) {
    return (
      <CardRefund
        bookingReference={bookingReference}
        currency={currency}
        totalPriceToBePaid={totalPriceToBePaid}
        loading={loading}
        paymentLocation={paymentLocation.name}
        isStation={paymentLocation.isStation}
        processCardPaymentRefund={processCardPaymentRefund}
        payments={payments}
        method={codes.GOOGLE_PAY}
        isCancelVoucher={isCancelVoucher}
        mainBookingRef={mainBookingRef}
        hasHotel={hasHotel}
      />
    );
  }

  if (method === codes.IDEAL) {
    return (
      <IdealRefund
        currency={currency}
        totalPriceToBePaid={totalPriceToBePaid}
        payments={findUniqueIdealPayments(payments)}
        totalAvailableRefundAmount={getAmountToRefundPerMethod(payments, codes.IDEAL)}
        addPayment={addPayment}
        addPayoutAndBalance={addPayoutAndBalance}
        bookingReference={bookingReference}
        method={method}
        reference={reference}
        updatePayments={updatePayments}
        isCancelVoucher={isCancelVoucher}
        mainBookingRef={mainBookingRef}
        processCardPaymentRefund={processCardPaymentRefund}
        paymentLocation={paymentLocation.name}
        hasHotel={hasHotel}
      />
    );
  }

  if (method === codes.SOFORT) {
    return (
      <SofortRefund
        currency={currency}
        totalPriceToBePaid={totalPriceToBePaid}
        payments={findUniqueSofortPayments(payments)}
        totalAvailableRefundAmount={getAmountToRefundPerMethod(payments, codes.SOFORT)}
        addPayment={addPayment}
        addPayoutAndBalance={addPayoutAndBalance}
        bookingReference={bookingReference}
        method={method}
        reference={reference}
        updatePayments={updatePayments}
        isCancelVoucher={isCancelVoucher}
        mainBookingRef={mainBookingRef}
        processCardPaymentRefund={processCardPaymentRefund}
        paymentLocation={paymentLocation.name}
        hasHotel={hasHotel}
      />
    );
  }

  if (method === codes.LOYALTY_POINTS) {
    return (
      <LoyaltyPointsRefund
        currency={currency}
        totalPriceToBePaid={totalPriceToBePaid}
        addPayment={addPayment}
        addPayoutAndBalance={addPayoutAndBalance}
        bookingReference={bookingReference}
        method={method}
        reference={reference}
        updatePayments={updatePayments}
        customer={customer}
        payments={payments}
        additionalDetails={additionalDetails}
        username={username}
        isOverpaid={isOverpaid}
      />
    );
  }

  return (
    <StandardSplitRefund
      addPayment={addPayment}
      addPayoutAndBalance={addPayoutAndBalance}
      bookingReference={bookingReference}
      currency={currency}
      customer={customer}
      loading={loading}
      method={method}
      reference={reference}
      title={<FormattedMessage id={`payments.AmountToRefundBy${method}`} />}
      totalPriceToBePaid={totalPriceToBePaid}
      updatePayments={updatePayments}
      isCancelVoucher={isCancelVoucher}
      mainBookingRef={mainBookingRef}
      isRefundingEntireBlueVoucherBooking={isRefundingEntireBlueVoucherBooking}
      payments={payments}
      isOverpaid={isOverpaid}
    />
  );
};

RefundComponents.propTypes = {
  addPayment: PropTypes.func,
  addPayoutAndBalance: PropTypes.func,
  bookingReference: PropTypes.string,
  currency: PropTypes.string,
  customer: customerShape,
  handleRefundSubmit: PropTypes.func,
  loading: PropTypes.bool,
  method: PropTypes.string,
  paymentLocation: PropTypes.shape({
    name: PropTypes.string,
    isStation: PropTypes.bool
  }).isRequired,
  processCardPaymentRefund: PropTypes.func.isRequired,
  reference: PropTypes.string,
  totalPriceToBePaid: PropTypes.number,
  updatePayments: PropTypes.func,
  payments: paymentsShape,
  isCancelVoucher: PropTypes.bool,
  mainBookingRef: PropTypes.string,
  isPackage: PropTypes.bool,
  hotels: hotelsShape,
  additionalDetails: PropTypes.arrayOf(
    PropTypes.shape({
      itemRef: PropTypes.string,
      key: PropTypes.string,
      value: PropTypes.string
    })
  ),
  username: PropTypes.string.isRequired,
  isRefundingEntireBlueVoucherBooking: PropTypes.bool,
  isOverpaid: PropTypes.bool
};

export default RefundComponents;
